/* ============================================================
   Hero Slider — autoplay, prev/next, dot navigation, fade/slide
   ============================================================ */

class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hero-slider__track');
    this.slides = Array.from(this.querySelectorAll('.hero-slider__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slider__dot'));
    this.prevBtn = this.querySelector('[data-direction="prev"]');
    this.nextBtn = this.querySelector('[data-direction="next"]');

    this.current = 0;
    this.total = this.slides.length;
    this.mode = this.dataset.transition || 'slide';
    this.autoplay = this.dataset.autoplay === 'true';
    this.defaultDuration = parseInt(this.dataset.autoplayDuration || '5', 10) * 1000;

    if (this.total < 2) return;

    this.bindEvents();
    this.applyHeightStyles();
    this.go(0, true);

    if (this.autoplay) this.startAutoplay();

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => { if (this.autoplay) this.startAutoplay(); });
  }

  applyHeightStyles() {
    // Parse height vars and apply to each slide's media area
    this.slides.forEach((slide) => {
      const styles = slide.style;
      const heightDesktop = styles.getPropertyValue('--hs-h-d').trim();
      const heightMobile = styles.getPropertyValue('--hs-h-m').trim();
      const media = slide;

      if (window.matchMedia('(min-width: 750px)').matches) {
        this.applyHeightString(media, heightDesktop);
      } else {
        this.applyHeightString(media, heightMobile);
      }
    });

    window.addEventListener('resize', () => {
      this.slides.forEach((slide) => {
        const heightDesktop = slide.style.getPropertyValue('--hs-h-d').trim();
        const heightMobile = slide.style.getPropertyValue('--hs-h-m').trim();
        if (window.matchMedia('(min-width: 750px)').matches) {
          this.applyHeightString(slide, heightDesktop);
        } else {
          this.applyHeightString(slide, heightMobile);
        }
      });
    });
  }

  applyHeightString(el, str) {
    if (!str) return;
    if (str.startsWith('height:')) {
      const val = str.replace('height:', '').trim();
      el.style.height = val;
      el.style.aspectRatio = '';
    } else if (str.startsWith('aspect-ratio:')) {
      const val = str.replace('aspect-ratio:', '').trim();
      el.style.aspectRatio = val;
      el.style.height = '';
    }
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.go(this.current - 1); this.resetAutoplay(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.go(this.current + 1); this.resetAutoplay(); });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.slideTarget, 10);
        this.go(idx);
        this.resetAutoplay();
      });
    });

    // touch swipe — buton/link üzerinde başlarsa swipe TETİKLEMEZ ve autoplay
    // TOUCH BOYUNCA DURUR (butonun altında slide değişmesin).
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let swipeArmed = false;
    let touchedLink = null;

    this.addEventListener('touchstart', (e) => {
      // Her touch'ta autoplay dursun — bir sonraki touchend'e kadar
      this.pauseAutoplay();

      // Etkileşim öğeleri (buton, link, form) üzerinde swipe yakalanmasın
      const link = e.target.closest('a[href], button');
      if (link) {
        touchedLink = link;
        swipeArmed = false;
        return;
      }
      touchedLink = null;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      endX = startX;
      swipeArmed = true;
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      if (!swipeArmed) {
        // Butonda başladıysa: parmak > 12px kaydıysa "iptal edildi" say —
        // link navigation TETİKLENMEYECEK, autoplay resume et
        if (touchedLink) {
          const dx = Math.abs(e.touches[0].clientX - (startX || e.touches[0].clientX));
          const dy = Math.abs(e.touches[0].clientY - (startY || e.touches[0].clientY));
          // startX/Y set edilmediği için ilk touchmove'da dx=0. Kaydet:
          if (startX === 0) {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
          } else if (dx > 12 || dy > 12) {
            touchedLink = null; // buton "release" — tıklama iptal
          }
        }
        return;
      }
      endX = e.touches[0].clientX;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      // Buton/link üzerinde bitiş — browser'ın click sentezini beklemek yerine
      // ELLE navigate et. Bazı browserlarda parmak 5-10px kaysa click iptal
      // ediliyor, bu yüzden link/buton çalışmıyor. Manuel garanti.
      if (touchedLink) {
        const link = touchedLink;
        touchedLink = null;
        if (link.tagName === 'A' && link.href && link.href !== window.location.href + '#') {
          e.preventDefault();
          // target="_blank" ise yeni sekme; değilse aynı sekme
          if (link.target === '_blank') window.open(link.href, '_blank');
          else window.location.href = link.href;
          return;
        }
        // Button ise native click'i tetikle
        if (link.tagName === 'BUTTON') {
          link.click();
          return;
        }
      }

      if (!swipeArmed) {
        // Autoplay resume et (kullanıcı slider'a değdi ama swipe/tıklama yok)
        if (this.autoplay) setTimeout(() => this.startAutoplay(), 400);
        return;
      }
      const diffX = startX - endX;
      const diffY = Math.abs(startY - (e.changedTouches[0]?.clientY ?? startY));
      // Yatay hareket dikeyden en az 1.5x büyük olmalı — dikey scroll ile karışmasın
      // ve threshold 60px (önceki 40 çok hassastı, dikkatsiz swipe tetikliyordu)
      if (Math.abs(diffX) > 60 && Math.abs(diffX) > diffY * 1.5) {
        if (diffX > 0) this.go(this.current + 1);
        else this.go(this.current - 1);
        this.resetAutoplay();
      } else if (this.autoplay) {
        setTimeout(() => this.startAutoplay(), 400);
      }
      startX = 0;
      startY = 0;
      endX = 0;
      swipeArmed = false;
    });
  }

  go(index, isInit = false) {
    let next = index;
    if (next < 0) next = this.total - 1;
    if (next >= this.total) next = 0;

    this.slides[this.current]?.classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');

    this.current = next;

    this.slides[this.current]?.classList.add('is-active');
    this.dots[this.current]?.classList.add('is-active');

    if (this.mode === 'slide') {
      this.track.style.transform = `translateX(-${this.current * 100}%)`;
    }
  }

  startAutoplay() {
    this.pauseAutoplay();
    const slideDuration = (parseInt(this.slides[this.current]?.dataset.slideDuration || '0', 10) * 1000) || this.defaultDuration;
    this._timer = setTimeout(() => {
      this.go(this.current + 1);
      this.startAutoplay();
    }, slideDuration);
  }

  pauseAutoplay() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplay) this.startAutoplay();
  }
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
